import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useApi, useWorkspace } from '~/contexts';

export default function useResourceMetrics({
  resources,
  start,
  end,
  unit,
  metric,
  adjustForTimeOff,
  allocations,
  onFetched,
}) {
  const viewed = useRef([]);
  const pending = useRef([]);
  const latest = useRef();

  const { workspace } = useWorkspace();
  const api = useApi();

  const fetchData = useCallback(
    async ({ start, end, unit, metric, adjustForTimeOff, resourceIds, allocationIds }) => {
      if (resourceIds.length === 0) return;

      const { data: metrics } = await api.www.workspaces(workspace.id).allocations().memberSchedule.metrics.resources({
        start,
        end,
        unit,
        metric,
        adjustForTimeOff,
        resourceIds,
        allocationIds,
      });

      if (metrics.members.length === 0 && metrics.placeholders.length === 0) return;

      onFetched(metrics);
    },
    [api, workspace.id, onFetched],
  );

  useEffect(() => {
    latest.current = { fetchData, start, end, unit, metric, adjustForTimeOff, allocations };
  });

  const flush = useMemo(
    () =>
      _.debounce(() => {
        const resourceIds = pending.current;
        pending.current = [];
        const { fetchData, allocations, ...params } = latest.current;
        const allocationIds = (allocations ?? []).filter((a) => resourceIds.includes(a.resourceId)).map((a) => a.id);
        fetchData({ ...params, allocationIds, resourceIds });
      }, 250),
    [],
  );

  useEffect(() => flush.cancel, [flush]);

  const load = useCallback(
    ({ start, end, unit, metric, adjustForTimeOff, allocations }) => {
      flush.cancel();
      const resourceIds = resources.map((r) => r.id);
      const allocationIds = (allocations ?? []).filter((a) => resourceIds.includes(a.resourceId)).map((a) => a.id);
      fetchData({ start, end, unit, metric, adjustForTimeOff, allocationIds, resourceIds });
      // Pending ids stay unviewed so the next effect run fetches them across the whole range.
      viewed.current = resourceIds.filter((id) => !pending.current.includes(id));
      pending.current = [];
    },
    [resources, fetchData, flush],
  );

  const initialize = useCallback(() => {
    flush.cancel();
    viewed.current = [];
    pending.current = [];
  }, [flush]);

  useEffect(() => {
    const resourceIds = resources.map((r) => r.id).filter((g) => !viewed.current.includes(g));
    if (resourceIds.length === 0) return;
    viewed.current = viewed.current.concat(resourceIds);
    pending.current = pending.current.concat(resourceIds);
    flush();
  }, [flush, start, end, unit, metric, adjustForTimeOff, allocations, resources]);

  return useMemo(() => ({ load, initialize }), [load, initialize]);
}
